import { useState, useEffect } from 'react';
import { PrivacyPolicyContent } from "@/components/PrivacyPolicyContent";
import { useNavigate } from 'react-router-dom';
import { SAFE_AREA } from "@/utils/safeArea";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  User, 
  Target, 
  Dumbbell, 
  BarChart3, 
  ArrowRight, 
  ArrowLeft,
  Zap,
  Calendar,
  Weight,
  Ruler,
  Plus,
  Trash2,
  Loader2
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useGoals, Goal } from '@/hooks/useGoals';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Analytics } from '@/lib/analytics';

import straivLogoDark from "@/assets/straiv-logo-dark.png";
import loadingGif from "@/assets/loading.gif";

export const Onboarding = () => {
  const navigate = useNavigate();
  const { profile, completeOnboarding, refetch: refetchProfile } = useProfile();
  const { user } = useAuth();
  const { createGoal } = useGoals();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [onboardingGoals, setOnboardingGoals] = useState<Omit<Goal, 'id' | 'created_at' | 'updated_at'>[]>([]);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal_type: 'strength',
    exercise_name: '',
    target_value: 0,
    target_unit: 'kg',
    current_value: 0,
    starting_value: null as number | null,
    target_date: '',
    is_active: true,
    description: ''
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    fitness_level: '',
    primary_goals: [] as string[],
    experience_years: null as number | null,
    height_cm: '',
    weight_kg: '',
    birth_date: '',
    work_type: '',
    work_type_custom: '',
    health_deficiencies: '',
    nutrition_restrictions: ''
  });
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);
  const [isSocialSignIn, setIsSocialSignIn] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Helper: Check if a name looks like a real name (not email-derived or random)
  const isValidRealName = (name: string | null | undefined, email: string | null | undefined): boolean => {
    if (!name || name.trim().length === 0) return false;
    
    const trimmed = name.trim();
    const emailPrefix = email?.split('@')[0]?.toLowerCase() || '';
    const lowerName = trimmed.toLowerCase();
    
    // Reject if it matches email prefix
    if (emailPrefix && lowerName === emailPrefix) {
      console.log(`Rejecting "${name}" - matches email prefix`);
      return false;
    }
    
    // Reject if it contains the email prefix (partial match)
    if (emailPrefix && emailPrefix.length > 3 && lowerName.includes(emailPrefix)) {
      console.log(`Rejecting "${name}" - contains email prefix`);
      return false;
    }
    
    // Reject if it contains numbers
    if (/\d/.test(trimmed)) {
      console.log(`Rejecting "${name}" - contains numbers`);
      return false;
    }
    
    // Reject if it's longer than 15 chars with no spaces (likely username)
    if (trimmed.length > 15 && !/\s/.test(trimmed)) {
      console.log(`Rejecting "${name}" - too long with no spaces`);
      return false;
    }
    
    // Reject if it looks like a random string (no vowels)
    const hasVowels = /[aeiouAEIOU]/.test(trimmed);
    if (!hasVowels && trimmed.length > 3) {
      console.log(`Rejecting "${name}" - no vowels (random string)`);
      return false;
    }
    
    console.log(`Accepting "${name}" as valid name`);
    return true;
  };

  // Track onboarding started
  useEffect(() => {
    Analytics.onboardingStarted();
  }, []);

  // Pre-populate form data from social sign-in (Apple/Google)
  useEffect(() => {
    const populateNameFromAuth = async () => {
      try {
        console.log('=== ONBOARDING NAME POPULATION ===');
        
        const { data: { user } } = await supabase.auth.getUser();
        const userEmail = user?.email || '';
        const provider = user?.app_metadata?.provider;
        
        // Track if this is a social sign-in (Apple/Google)
        const socialProviders = ['apple', 'google'];
        if (provider && socialProviders.includes(provider)) {
          setIsSocialSignIn(true);
          console.log('Social sign-in detected:', provider);
        }
        
        // FIRST: Check sessionStorage for names from social sign-in
        // This is set immediately during Apple/Google sign-in to avoid race conditions
        const storedFirstName = sessionStorage.getItem('social_signin_first_name');
        const storedLastName = sessionStorage.getItem('social_signin_last_name');
        
        console.log('SessionStorage firstName:', storedFirstName);
        console.log('SessionStorage lastName:', storedLastName);
        
        // Validate sessionStorage names
        if (storedFirstName && isValidRealName(storedFirstName, userEmail)) {
          console.log('Using sessionStorage names:', storedFirstName, storedLastName);
          setFormData(prev => ({
            ...prev,
            first_name: storedFirstName,
            last_name: isValidRealName(storedLastName, userEmail) ? storedLastName || '' : '',
          }));
          // Clear sessionStorage after reading
          sessionStorage.removeItem('social_signin_first_name');
          sessionStorage.removeItem('social_signin_last_name');
          console.log('=================================');
          return;
        }
        
        // Clear any invalid sessionStorage data
        sessionStorage.removeItem('social_signin_first_name');
        sessionStorage.removeItem('social_signin_last_name');
        
        // SECOND: Check profile (for returning users)
        if (profile?.first_name && isValidRealName(profile.first_name, userEmail)) {
          console.log('Using profile name:', profile.first_name, profile.last_name);
          setFormData(prev => ({
            ...prev,
            first_name: profile.first_name || '',
            last_name: isValidRealName(profile.last_name, userEmail) ? profile.last_name || '' : '',
          }));
          console.log('=================================');
          return;
        }
        
        // THIRD: Check Supabase user metadata (for web OAuth)
        console.log('User email:', userEmail);
        console.log('User metadata:', JSON.stringify(user?.user_metadata));
        
        if (user?.user_metadata) {
          const metadata = user.user_metadata;
          let firstName = metadata.given_name || metadata.first_name || null;
          let lastName = metadata.family_name || metadata.last_name || null;
          
          // Try to split full_name if no valid first/last
          if ((!firstName || !isValidRealName(firstName, userEmail)) && (metadata.full_name || metadata.name)) {
            const fullName = metadata.full_name || metadata.name;
            if (fullName && fullName.includes(' ') && isValidRealName(fullName, userEmail)) {
              const nameParts = fullName.trim().split(' ');
              firstName = nameParts[0] || null;
              lastName = nameParts.slice(1).join(' ') || null;
            }
          }
          
          console.log('Extracted from metadata - firstName:', firstName, 'lastName:', lastName);
          
          // Only populate if names pass validation
          if (firstName && isValidRealName(firstName, userEmail)) {
            setFormData(prev => ({
              ...prev,
              first_name: firstName,
              last_name: isValidRealName(lastName, userEmail) ? lastName || '' : '',
            }));
          } else {
            console.log('No valid name found, leaving fields empty for user to enter');
          }
        }
        console.log('=================================');
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    populateNameFromAuth();
  }, [profile]);

  const totalSteps = 7;
  const progress = (currentStep / totalSteps) * 100;

  const fitnessLevels = [
    { value: 'beginner', label: 'Beginner', desc: 'New to fitness or returning after a break' },
    { value: 'intermediate', label: 'Intermediate', desc: '6+ months of consistent training' },
    { value: 'advanced', label: 'Advanced', desc: '2+ years of serious training' },
    { value: 'elite', label: 'Elite', desc: 'Competitive athlete or coach' }
  ];

  const primaryGoals = [
    'Build Muscle',
    'Lose Weight',
    'Improve Strength',
    'Increase Endurance',
    'Better Health',
    'Sport Performance',
    'Flexibility',
    'Mental Wellness'
  ];

  const handleGoalToggle = (goal: string) => {
    setFormData(prev => ({
      ...prev,
      primary_goals: prev.primary_goals.includes(goal)
        ? prev.primary_goals.filter(g => g !== goal)
        : [...prev.primary_goals, goal]
    }));
    // Clear error when user selects a goal
    if (validationErrors.primary_goals) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next.primary_goals;
        return next;
      });
    }
  };

  const stepNames = ['Basic Info', 'Fitness Level', 'Overall Goals', 'Specific Goals', 'Work Type', 'Health Conditions', 'Dietary Restrictions'];
  
  const handleNext = () => {
    if (currentStep < totalSteps) {
      // If leaving Step 4 (Specific Goals) with an in-progress goal, auto-add it
      if (currentStep === 4 && showGoalDialog && newGoal.exercise_name && newGoal.target_value > 0) {
        setOnboardingGoals(prev => [...prev, { ...newGoal, starting_value: newGoal.current_value, user_id: '' }]);
        setNewGoal({
          goal_type: 'strength',
          exercise_name: '',
          target_value: 0,
          target_unit: 'kg',
          current_value: 0,
          starting_value: null,
          target_date: '',
          is_active: true,
          description: ''
        });
        setShowGoalDialog(false);
      }
      // Track step completion
      Analytics.onboardingStepCompleted(currentStep, stepNames[currentStep - 1] || `Step ${currentStep}`);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleAddGoal = () => {
    if (newGoal.exercise_name && newGoal.target_value > 0) {
      setOnboardingGoals(prev => [...prev, { ...newGoal, starting_value: newGoal.current_value, user_id: '' }]);
      setNewGoal({
        goal_type: 'strength',
        exercise_name: '',
        target_value: 0,
        target_unit: 'kg',
        current_value: 0,
        starting_value: null,
        target_date: '',
        is_active: true,
        description: ''
      });
      setShowGoalDialog(false);
      toast.success('Goal added!');
    }
  };

  const handleRemoveGoal = (index: number) => {
    setOnboardingGoals(prev => prev.filter((_, i) => i !== index));
  };

  // Validation function with detailed error messages
  const validateForm = (): { isValid: boolean; errors: string[]; fieldErrors: Record<string, string> } => {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Step 1: Basic Info - Gender is required
    if (!formData.gender || formData.gender.trim() === '') {
      errors.push('Please select your gender');
      fieldErrors.gender = 'Gender is required';
    }

    // Step 2: Fitness Level - Required
    if (!formData.fitness_level || formData.fitness_level.trim() === '') {
      errors.push('Please select your fitness level');
      fieldErrors.fitness_level = 'Fitness level is required';
    }

    // Step 3: Primary Goals - At least one required
    if (!formData.primary_goals || formData.primary_goals.length === 0) {
      errors.push('Please select at least one primary goal');
      fieldErrors.primary_goals = 'At least one goal is required';
    }

    // Step 5: Work Type - Required
    if (!formData.work_type || formData.work_type.trim() === '') {
      errors.push('Please select your work type');
      fieldErrors.work_type = 'Work type is required';
    }
    if (formData.work_type === 'other' && (!formData.work_type_custom || formData.work_type_custom.trim() === '')) {
      errors.push('Please specify your work type');
      fieldErrors.work_type_custom = 'Please specify your work type';
    }

    // Step 7: Data Consent - Required
    if (!dataConsentAccepted) {
      errors.push('Please accept the data collection consent to continue');
      fieldErrors.dataConsent = 'Data consent is required';
    }

    // Validate birth date format if provided
    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      if (isNaN(birthDate.getTime())) {
        errors.push('Please enter a valid birth date');
        fieldErrors.birth_date = 'Invalid date format';
      } else if (birthDate > today) {
        errors.push('Birth date cannot be in the future');
        fieldErrors.birth_date = 'Birth date cannot be in the future';
      } else {
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 13) {
          errors.push('You must be at least 13 years old to use this app');
          fieldErrors.birth_date = 'You must be at least 13 years old';
        } else if (age > 120) {
          errors.push('Please enter a valid birth date');
          fieldErrors.birth_date = 'Please enter a valid birth date';
        }
      }
    }

    // Validate height if provided
    if (formData.height_cm) {
      const height = parseInt(formData.height_cm);
      if (isNaN(height) || height < 50 || height > 250) {
        errors.push('Please enter a valid height (50-250 cm)');
        fieldErrors.height_cm = 'Height must be between 50-250 cm';
      }
    }

    // Validate weight if provided
    if (formData.weight_kg) {
      const weight = parseFloat(formData.weight_kg);
      if (isNaN(weight) || weight < 20 || weight > 500) {
        errors.push('Please enter a valid weight (20-500 kg)');
        fieldErrors.weight_kg = 'Weight must be between 20-500 kg';
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors
    };
  };

  const handleSubmit = async () => {
    // Validate form before submission
    const validation = validateForm();
    if (!validation.isValid) {
      // Set field errors for inline display
      setValidationErrors(validation.fieldErrors);
      
      // Show first error as main toast, others as secondary
      toast.error(validation.errors[0] || 'Please correct the errors below', {
        description: validation.errors.length > 1 
          ? `${validation.errors.length - 1} more error${validation.errors.length > 2 ? 's' : ''} to fix`
          : undefined
      });
      
      // Scroll to first error field if possible
      const firstErrorField = Object.keys(validation.fieldErrors)[0];
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }
      return;
    }

    // Clear any previous validation errors
    setValidationErrors({});

    setIsSubmitting(true);
    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        // Map work_type to activity_level
        let activity_level = 'moderate'; // default
        if (formData.work_type === 'student' || formData.work_type === 'office' || formData.work_type === 'remote') {
          activity_level = 'sedentary';
        } else if (formData.work_type === 'physical') {
          activity_level = 'active';
        }

        // Calculate age from birth_date
        let age = null;
        if (formData.birth_date) {
          const birthDate = new Date(formData.birth_date);
          const today = new Date();
          age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
        }

        // Create all onboarding goals (also include any in-progress goal not explicitly added)
        const goalsToCreate = [...onboardingGoals];
        if (newGoal.exercise_name && newGoal.target_value > 0) {
          goalsToCreate.push({ ...newGoal, starting_value: newGoal.current_value });
        }

        // Auto-create weight goal if "Lose Weight" is selected and weight is provided
        const currentWeight = formData.weight_kg ? parseFloat(formData.weight_kg) : null;
        if (formData.primary_goals.includes('Lose Weight') && currentWeight && currentWeight > 0) {
          // Calculate target weight (10% reduction or at least 5kg less)
          const targetWeight = Math.max(currentWeight * 0.9, currentWeight - 5);
          goalsToCreate.push({
            goal_type: 'weight',
            exercise_name: 'Body Weight',
            target_value: Math.round(targetWeight * 10) / 10,
            target_unit: 'kg',
            current_value: currentWeight,
            starting_value: currentWeight,
            target_date: null,
            is_active: true,
            description: 'Weight loss goal from onboarding'
          } as any);
        }

        // Format specific goals as text for profile.specific_goals
        const specificGoalsText = goalsToCreate
          .filter(goal => goal.exercise_name && goal.target_value > 0)
          .map(goal => {
            const exerciseName = goal.exercise_name || '';
            const targetValue = goal.target_value || 0;
            const unit = goal.target_unit || '';
            return `${exerciseName} ${targetValue}${unit}`;
          })
          .join(', ');

        const profileData = {
          ...formData,
          activity_level,
          age,
          height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
          weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
          birth_date: formData.birth_date || null,
          tutorial_completed: false,
          specific_goals: specificGoalsText || null,
        };

        // Attempt to complete onboarding with retry logic
        const { error } = await completeOnboarding(profileData);
        
        if (error) {
          retryCount++;
          if (retryCount < maxRetries) {
            // Wait before retry with exponential backoff
            const delay = Math.pow(2, retryCount) * 500; // 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, delay));
            continue; // Retry
          } else {
            // All retries failed
            console.error('[Onboarding] Failed after retries:', error);
            toast.error('Failed to save your profile', {
              description: error.message || 'Please check your internet connection and try again. If the problem persists, contact support.'
            });
            Analytics.errorOccurred('onboarding_database_error', 'onboarding');
            setIsSubmitting(false);
            return;
          }
        }

        // Successfully saved to database - verify by refetching profile
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait for database to update
        await refetchProfile();
        
        // Double-check profile was updated (non-blocking - don't fail if verification fails)
        if (user) {
          const { data: verifyProfile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (verifyProfile && !verifyProfile.onboarding_completed) {
            console.warn('[Onboarding] Profile verification failed, but continuing...');
            // Try one more time
            const { error: retryError } = await completeOnboarding(profileData);
            if (retryError) {
              throw new Error('Profile verification failed after retry');
            }
          }
        }

        // Create goals (non-blocking - don't fail onboarding if goals fail)
        for (const goal of goalsToCreate) {
          try {
            await createGoal(goal);
          } catch (gErr) {
            console.error('Failed to create goal:', gErr);
            // Don't block onboarding completion for goal creation failures
          }
        }

        // Mark onboarding done on client to avoid immediate redirect loops
        localStorage.setItem('onboarding_completed', 'true');
        // Ensure tutorial shows next
        localStorage.setItem('tutorial_completed', 'false');
        
        // Track onboarding completion (non-blocking - wrapped in try-catch to prevent failures)
        // Analytics should NEVER block onboarding completion
        try {
          Analytics.onboardingCompleted();
        } catch (analyticsError: any) {
          console.warn('[Onboarding] Analytics.onboardingCompleted error (non-critical):', analyticsError);
        }
        
        // Set user properties for segmentation (non-blocking - don't fail onboarding if this fails)
        try {
          if (formData.fitness_level) {
            await Analytics.setUserProperty('fitness_level', formData.fitness_level);
          }
          if (formData.primary_goals.length > 0) {
            await Analytics.setUserProperty('primary_goal', formData.primary_goals[0]);
          }
        } catch (analyticsError: any) {
          // Log but don't fail onboarding if analytics fails
          console.warn('[Onboarding] Analytics.setUserProperty error (non-critical):', analyticsError);
        }
        
        // Success - navigate to next step
        toast.success('Profile created successfully!', {
          description: 'Setting up your personalized experience...'
        });
        
        // Add a slight delay to show the success message and ensure profile is updated
        setTimeout(() => {
          setIsSubmitting(false);
          navigate('/educational-slides', { replace: true });
        }, 1000);
        return; // Success - exit retry loop

      } catch (error: any) {
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 500;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        } else {
          console.error('[Onboarding] Submit error:', error);
          toast.error('Something went wrong', {
            description: error.message || 'Please try again. If the problem persists, contact support.'
          });
          Analytics.errorOccurred('onboarding_failed', 'onboarding');
          setIsSubmitting(false);
          return;
        }
      }
    }
  };
  const canProceed = () => {
    // Step 1: Basic Info - Gender required
    if (currentStep === 1) {
      return formData.gender && formData.gender.trim() !== '';
    }
    // Step 2: Fitness Level - Required
    if (currentStep === 2) {
      return formData.fitness_level && formData.fitness_level.trim() !== '';
    }
    // Step 3: Primary Goals - At least one required
    if (currentStep === 3) {
      return formData.primary_goals && formData.primary_goals.length > 0;
    }
    // Step 5: Work Type - Required
    if (currentStep === 5) {
      if (!formData.work_type || formData.work_type.trim() === '') return false;
      if (formData.work_type === 'other' && (!formData.work_type_custom || formData.work_type_custom.trim() === '')) {
        return false;
      }
      return true;
    }
    // Step 7: Data Consent - Required
    if (currentStep === 7) {
      return dataConsentAccepted;
    }
    // Steps 4 and 6 are optional
    return true;
  };

  return (
    <div 
      className="min-h-screen bg-gradient-bg flex items-center justify-center p-4 pb-safe" 
      style={{ 
        paddingTop: `max(${SAFE_AREA.top}, 16px)`,
        paddingBottom: `max(${SAFE_AREA.bottom}, 16px)`,
      }}
    >
      {isSubmitting && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-6 animate-fade-in">
          <img 
            src={loadingGif} 
            alt="Loading" 
            className="h-32 w-32 object-contain"
          />
          <div className="text-center space-y-2 animate-fade-in">
            <h3 className="text-2xl font-bold text-foreground">Creating Your Profile</h3>
            <p className="text-muted-foreground">Setting up your personalized fitness journey...</p>
          </div>
        </div>
      )}
      <Card 
        className="w-full max-w-md bg-gradient-card border-border/50 shadow-card flex flex-col"
        style={{ maxHeight: `calc(100vh - max(${SAFE_AREA.top}, 32px) - max(${SAFE_AREA.bottom}, 32px) - 32px)` }}
      >
        <CardHeader className="text-center space-y-3 flex-shrink-0 pb-3">
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-xl font-bold">Welcome to</span>
              <img 
                src={straivLogoDark}
                alt="STRAIV" 
                className="h-28 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-muted-foreground">Let's personalize your fitness journey</p>
          </div>
          <div className="space-y-1">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">Step {currentStep} of {totalSteps}</p>
          </div>
        </CardHeader>

        <CardContent 
          className="space-y-4 overflow-y-auto flex-1"
          style={{ 
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Tell us about yourself</h3>
              </div>
              
              {isSocialSignIn && (
                <p className="text-xs text-muted-foreground -mt-2">
                  Name fields are optional when using Apple or Google sign-in.
                </p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    First Name {isSocialSignIn && <span className="text-muted-foreground font-normal">(Optional)</span>}
                  </Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder={isSocialSignIn ? "Optional" : "Enter your first name"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">
                    Last Name {isSocialSignIn && <span className="text-muted-foreground font-normal">(Optional)</span>}
                  </Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder={isSocialSignIn ? "Optional" : "Enter your last name"}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select 
                    value={formData.gender || ''} 
                    onValueChange={(value) => {
                      setFormData(prev => ({ ...prev, gender: value }));
                      // Clear error when user selects
                      if (validationErrors.gender) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.gender;
                          return next;
                        });
                      }
                    }}
                  >
                    <SelectTrigger id="gender" className={validationErrors.gender ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                  {validationErrors.gender && (
                    <p className="text-sm text-destructive">{validationErrors.gender}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Birth Date</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {/* Year selector - most important for age */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[0] : ''}
                        onValueChange={(year) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : ['', '01', '01'];
                          const newDate = `${year}-${parts[1] || '01'}-${parts[2] || '01'}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - 13 - i; // Start from 13 years ago
                            return (
                              <SelectItem key={year} value={year.toString()}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      
                      {/* Month selector */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[1] : ''}
                        onValueChange={(month) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : [new Date().getFullYear().toString(), '', '01'];
                          const newDate = `${parts[0]}-${month}-${parts[2] || '01'}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, i) => (
                            <SelectItem key={i} value={(i + 1).toString().padStart(2, '0')}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Day selector */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[2] : ''}
                        onValueChange={(day) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : [new Date().getFullYear().toString(), '01', ''];
                          const newDate = `${parts[0]}-${parts[1]}-${day}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Day" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString().padStart(2, '0')}>
                              {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {validationErrors.birth_date && (
                      <p className="text-sm text-destructive">{validationErrors.birth_date}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height_cm">Height (cm)</Label>
                    <div className="relative">
                      <Ruler className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="height_cm"
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.height_cm}
                        onChange={(e) => setFormData(prev => ({ ...prev, height_cm: e.target.value }))}
                        onFocus={(e) => e.target.select()}
                        placeholder="170"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight_kg">Weight (kg)</Label>
                    <div className="relative">
                      <Weight className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="weight_kg"
                        type="text"
                        inputMode="decimal"
                        value={formData.weight_kg}
                        onChange={(e) => {
                          const value = e.target.value.replace(',', '.');
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            setFormData(prev => ({ ...prev, weight_kg: value }));
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        placeholder="70"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Fitness Level */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">What's your fitness level?</h3>
              </div>
              
              <div className="grid gap-2">
                {fitnessLevels.map((level) => (
                  <Card 
                    key={level.value}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.fitness_level === level.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, fitness_level: level.value }))}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-start gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 ${
                          formData.fitness_level === level.value 
                            ? 'border-primary bg-primary' 
                            : 'border-muted'
                        }`} />
                        <div>
                          <h4 className="text-sm font-semibold">{level.label}</h4>
                          <p className="text-xs text-muted-foreground">{level.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="experience_years">Years of Training Experience (Optional)</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="experience_years"
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    max="50"
                    value={formData.experience_years ?? ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, experience_years: e.target.value === '' ? null : parseInt(e.target.value) }))}
                    placeholder="How many years have you been training?"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Overall Goals */}
          {currentStep === 3 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Overall Goals</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Select your primary fitness goals (used for nutrition and your coach's advice)
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                {primaryGoals.map((goal) => (
                  <Card
                    key={goal}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.primary_goals.includes(goal)
                        ? 'border-primary bg-primary/5'
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => handleGoalToggle(goal)}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={formData.primary_goals.includes(goal)}
                          onCheckedChange={() => handleGoalToggle(goal)}
                          className="pointer-events-none h-4 w-4"
                        />
                        <span className="text-sm font-medium">{goal}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {validationErrors.primary_goals && (
                <p className="text-sm text-destructive">{validationErrors.primary_goals}</p>
              )}
            </div>
          )}

          {/* Step 4: Specific Goals */}
          {currentStep === 4 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Specific Goals (Optional)</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Add specific, measurable goals to track your progress
              </p>

              {onboardingGoals.length > 0 && (
                <div className="space-y-2 mb-4">
                  {onboardingGoals.map((goal, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{goal.exercise_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Target: {goal.target_value} {goal.target_unit}
                          {goal.target_date && ` by ${new Date(goal.target_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGoal(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {!showGoalDialog && (
                <Button
                  variant="outline"
                  onClick={() => setShowGoalDialog(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Fitness Goal
                </Button>
              )}

              {showGoalDialog && (
                <Card className="border-primary/50">
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <Label>Goal Type</Label>
                      <div className="px-3 py-2 bg-muted/50 rounded-md text-sm font-medium">
                        Strength Goal
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Exercise Name</Label>
                      <Input
                        value={newGoal.exercise_name}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, exercise_name: e.target.value }))}
                        placeholder="e.g., Bench Press, Squat, Deadlift"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Target Value</Label>
                        <Input
                          type="number"
                          value={newGoal.target_value || ''}
                          onChange={(e) => setNewGoal(prev => ({ ...prev, target_value: parseFloat(e.target.value) || 0 }))}
                          placeholder="100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Select
                          value={newGoal.target_unit}
                          onValueChange={(value) => setNewGoal(prev => ({ ...prev, target_unit: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="lbs">lbs</SelectItem>
                            <SelectItem value="reps">reps</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                            <SelectItem value="km">km</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Current Value (Optional)</Label>
                      <Input
                        type="number"
                        value={newGoal.current_value || ''}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, current_value: parseFloat(e.target.value) || 0 }))}
                        placeholder="80"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Target Date (Optional)</Label>
                      <Input
                        type="date"
                        value={newGoal.target_date}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, target_date: e.target.value }))}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleAddGoal} className="flex-1">
                        Add Goal
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowGoalDialog(false);
                          setNewGoal({
                            goal_type: 'strength',
                            exercise_name: '',
                            target_value: 0,
                            target_unit: 'kg',
                            current_value: 0,
                            starting_value: null,
                            target_date: '',
                            is_active: true,
                            description: ''
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {onboardingGoals.length === 0 && !showGoalDialog && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No specific goals added yet. You can skip this step or add goals now.
                </p>
              )}
            </div>
          )}

          {/* Step 5: Work Type */}
          {currentStep === 5 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">What type of work do you do?</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">This helps us understand your daily activity level</p>
              
              <div className="space-y-2">
                {[
                  { value: 'student', label: 'Student' },
                  { value: 'office', label: 'Office Work' },
                  { value: 'physical', label: 'Physical Work' },
                  { value: 'remote', label: 'Work From Home' },
                  { value: 'other', label: 'Other' },
                ].map((type) => (
                  <Card 
                    key={type.value}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.work_type === type.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, work_type: type.value }));
                      // Clear error when user selects
                      if (validationErrors.work_type) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.work_type;
                          return next;
                        });
                      }
                    }}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 ${
                          formData.work_type === type.value 
                            ? 'border-primary bg-primary' 
                            : 'border-muted'
                        }`} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {formData.work_type === 'other' && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="work_type_custom">Please specify your work type</Label>
                    <Input
                      id="work_type_custom"
                      value={formData.work_type_custom}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, work_type_custom: e.target.value }));
                        // Clear error when user types
                        if (validationErrors.work_type_custom) {
                          setValidationErrors(prev => {
                            const next = { ...prev };
                            delete next.work_type_custom;
                            return next;
                          });
                        }
                      }}
                      placeholder="Describe your work"
                      className={validationErrors.work_type_custom ? 'border-destructive' : ''}
                    />
                    {validationErrors.work_type_custom && (
                      <p className="text-sm text-destructive">{validationErrors.work_type_custom}</p>
                    )}
                  </div>
                )}
              </div>
              {validationErrors.work_type && (
                <p className="text-sm text-destructive">{validationErrors.work_type}</p>
              )}
            </div>
          )}

          {/* Step 6: Health Deficiencies */}
          {currentStep === 6 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">Health conditions?</h3>
                  <p className="text-xs text-muted-foreground">Helps us tailor your training (Optional)</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="health_deficiencies">Health Information</Label>
                <Textarea
                  id="health_deficiencies"
                  value={formData.health_deficiencies}
                  onChange={(e) => setFormData(prev => ({ ...prev, health_deficiencies: e.target.value }))}
                  placeholder="e.g., Asthma, joint issues, vitamin deficiencies, injuries, etc."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  Your information is private and will only be used to personalize your experience
                </p>
              </div>
            </div>
          )}

          {/* Step 7: Nutrition Restrictions */}
          {currentStep === 7 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">Dietary restrictions?</h3>
                  <p className="text-xs text-muted-foreground">Helps with nutrition recommendations (Optional)</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nutrition_restrictions">Dietary Restrictions & Allergies</Label>
                <Textarea
                  id="nutrition_restrictions"
                  value={formData.nutrition_restrictions}
                  onChange={(e) => setFormData(prev => ({ ...prev, nutrition_restrictions: e.target.value }))}
                  placeholder="e.g., Vegetarian, vegan, lactose intolerant, peanut allergy, gluten-free, kosher, halal, etc."
                  rows={3}
                />
              </div>

              {/* Data Collection Consent */}
              <div className={`mt-4 p-3 bg-muted/50 rounded-lg border ${validationErrors.dataConsent ? 'border-destructive' : 'border-border/50'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="data-consent"
                    checked={dataConsentAccepted}
                    onCheckedChange={(checked) => {
                      setDataConsentAccepted(checked === true);
                      // Clear error when user accepts
                      if (validationErrors.dataConsent) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.dataConsent;
                          return next;
                        });
                      }
                    }}
                    className="mt-0.5"
                  />
                  <Label htmlFor="data-consent" className="text-sm leading-relaxed cursor-pointer">
                    I consent to the collection and processing of my fitness, nutrition, and health data to personalize my experience. 
                    I understand this data is stored securely and used only to provide AI coaching, progress tracking, and personalized recommendations. 
                    View our{' '}
                    <Dialog>
                      <DialogTrigger asChild>
                        <span className="text-primary underline cursor-pointer">Privacy Policy</span>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>Privacy Policy</DialogTitle>
                        </DialogHeader>
                        <ScrollArea className="h-[60vh] pr-4">
                          <PrivacyPolicyContent compact />
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>.
                  </Label>
                </div>
              </div>
              {validationErrors.dataConsent && (
                <p className="text-sm text-destructive mt-2">{validationErrors.dataConsent}</p>
              )}
            </div>
          )}

        </CardContent>

        {/* Navigation - Fixed at bottom of card */}
        <div className="flex justify-between p-4 pt-3 border-t border-border/50 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSubmitting}
            className="flex items-center gap-2"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
              className="flex items-center gap-2"
              size="sm"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="flex items-center gap-2 bg-gradient-primary text-black border-0"
              size="sm"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  Complete Setup
                  <Zap className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};